import { useEffect, useRef, useState, useCallback } from "react";
import { BUILD_VERSION } from "../buildInfo";

const POLL_INTERVAL_MS = 60_000;
const VERSION_URL = `${process.env.PUBLIC_URL || ""}/version.json`;

type RemoteVersion = {
  version?: string;
  buildTime?: string;
  commitHash?: string;
};

async function fetchRemoteVersion(): Promise<RemoteVersion | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    return (await res.json()) as RemoteVersion;
  } catch {
    return null;
  }
}

export async function clearAppCachesAndReload(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore — best-effort
  }
  const url = new URL(window.location.href);
  url.searchParams.set("v", Date.now().toString());
  window.location.replace(url.toString());
}

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<string | null>(null);
  const stoppedRef = useRef(false);

  const check = useCallback(async () => {
    const remote = await fetchRemoteVersion();
    if (!remote?.version) return;
    if (remote.version !== BUILD_VERSION) {
      setRemoteVersion(remote.version);
      setUpdateAvailable(true);
      stoppedRef.current = true;
    }
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    check();
    timer = setInterval(() => {
      if (stoppedRef.current) return;
      check();
    }, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !stoppedRef.current) {
        check();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [check]);

  const reload = useCallback(() => {
    void clearAppCachesAndReload();
  }, []);

  return {
    updateAvailable,
    currentVersion: BUILD_VERSION,
    remoteVersion,
    reload,
  };
}
